import { Component, OnInit, inject, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PayablesService } from '../../core/services/payables.service';
import { SuppliersService } from '../../core/services/suppliers.service';
import { PayableDocument, CreatePayableDto, UpdatePayableDto, PayableFilter, PaymentStatus, DocumentType, PagedResult, PayableAllocation } from '../../core/models/payable.model';
import { Supplier } from '../../core/models/supplier.model';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormBuilder, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'app-payables',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    DecimalPipe,
    DatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatChipsModule
  ],
  template: `
    <div class="page-header">
      <div>
        <h1 class="page-title">Dugovanja</h1>
        <p class="page-subtitle" *ngIf="selectedSupplier">
          Dobavljač: <strong>{{ selectedSupplier.name }}</strong>
        </p>
        <p class="page-subtitle" *ngIf="!selectedSupplier">
          Pregled svih dugovanja prema dobavljačima
        </p>
      </div>
      <div class="header-actions">
        <button mat-stroked-button (click)="goToPayments()" class="view-payments-button">
          <mat-icon>list</mat-icon>
          Sve uplate
        </button>
        <button mat-raised-button color="primary" (click)="openCreate()" class="create-button">
          <mat-icon>add</mat-icon>
          Novo dugovanje
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-container">
      <div class="filters-row">
        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Dobavljač</mat-label>
          <mat-select [(ngModel)]="filter.supplierId" (selectionChange)="load()">
            <mat-option [value]="undefined">Svi dobavljači</mat-option>
            <mat-option *ngFor="let s of suppliers" [value]="s.id">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="filter.status" (selectionChange)="load()">
            <mat-option [value]="undefined">Svi</mat-option>
            <mat-option [value]="0">Neplaćeno</mat-option>
            <mat-option [value]="1">Delimično plaćeno</mat-option>
            <mat-option [value]="2">Plaćeno</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Pretraži</mat-label>
          <input matInput [(ngModel)]="filter.searchText" (keyup.enter)="load()" placeholder="Broj računa, napomena...">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <mat-slide-toggle [(ngModel)]="filter.overdueOnly" (change)="load()">
          Samo dospela
        </mat-slide-toggle>
      </div>

      <div class="filters-row date-filters">
        <mat-form-field appearance="outline" class="filter-field-date">
          <mat-label>Datum od</mat-label>
          <input matInput [matDatepicker]="pickerFrom" [(ngModel)]="invoiceDateFrom" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="pickerFrom"></mat-datepicker-toggle>
          <mat-datepicker #pickerFrom></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field-date">
          <mat-label>Datum do</mat-label>
          <input matInput [matDatepicker]="pickerTo" [(ngModel)]="invoiceDateTo" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="pickerTo"></mat-datepicker-toggle>
          <mat-datepicker #pickerTo></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field-date">
          <mat-label>Valuta od</mat-label>
          <input matInput [matDatepicker]="pickerDueFrom" [(ngModel)]="dueDateFrom" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="pickerDueFrom"></mat-datepicker-toggle>
          <mat-datepicker #pickerDueFrom></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field-date">
          <mat-label>Valuta do</mat-label>
          <input matInput [matDatepicker]="pickerDueTo" [(ngModel)]="dueDateTo" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="pickerDueTo"></mat-datepicker-toggle>
          <mat-datepicker #pickerDueTo></mat-datepicker>
        </mat-form-field>

        <button mat-stroked-button (click)="clearDateFilters()" *ngIf="hasDateFilters()" class="clear-btn">
          <mat-icon>clear</mat-icon>
          Očisti datume
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div class="loading-container" *ngIf="loading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <!-- Table -->
    <div class="table-container" *ngIf="!loading && data.items.length">
      <table mat-table [dataSource]="data.items" class="payables-table">
        <ng-container matColumnDef="supplierName">
          <th mat-header-cell *matHeaderCellDef>Dobavljač</th>
          <td mat-cell *matCellDef="let p">{{ p.supplierNameSnapshot }}</td>
        </ng-container>
        <ng-container matColumnDef="documentType">
          <th mat-header-cell *matHeaderCellDef>Tip</th>
          <td mat-cell *matCellDef="let p">{{ getDocumentTypeLabel(p.documentType) }}</td>
        </ng-container>
        <ng-container matColumnDef="invoiceNumber">
          <th mat-header-cell *matHeaderCellDef>Račun broj</th>
          <td mat-cell *matCellDef="let p">{{ p.invoiceNumber || '-' }}</td>
        </ng-container>
        <ng-container matColumnDef="deliveryNoteNumber">
          <th mat-header-cell *matHeaderCellDef>Otpremnica</th>
          <td mat-cell *matCellDef="let p">{{ p.deliveryNoteNumber || '-' }}</td>
        </ng-container>
        <ng-container matColumnDef="invoiceDate">
          <th mat-header-cell *matHeaderCellDef>Datum</th>
          <td mat-cell *matCellDef="let p">{{ p.invoiceDate | date:'dd.MM.yyyy' }}</td>
        </ng-container>
        <ng-container matColumnDef="dueDate">
          <th mat-header-cell *matHeaderCellDef>Datum valute</th>
          <td mat-cell *matCellDef="let p" [ngClass]="{'overdue-date': p.isOverdue}">
            {{ p.dueDate | date:'dd.MM.yyyy' }}
            <mat-icon *ngIf="p.isOverdue" class="overdue-icon" matTooltip="Dospelo za naplatu">warning</mat-icon>
          </td>
        </ng-container>
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Iznos</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell">{{ p.amount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="paidAmount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Plaćeno</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell paid">{{ p.paidAmount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="remainingAmount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Preostalo</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell" [ngClass]="{'remaining': p.remainingAmount > 0}">
            {{ p.remainingAmount | number:'1.2-2' }}
          </td>
        </ng-container>
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef class="text-center">Status</th>
          <td mat-cell *matCellDef="let p" class="text-center">
            <span class="status-chip" [ngClass]="getStatusClass(p.status)">
              {{ getStatusLabel(p.status) }}
            </span>
          </td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">Akcije</th>
          <td mat-cell *matCellDef="let p" class="table-actions">
            <button mat-icon-button (click)="viewPayments(p)" matTooltip="Pregled uplata" class="action-button"
                    [disabled]="p.paidAmount === 0">
              <mat-icon>receipt_long</mat-icon>
            </button>
            <button mat-icon-button (click)="openEdit(p)" matTooltip="Izmeni" class="action-button">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button (click)="confirmDelete(p)" matTooltip="Obriši" class="action-button delete-button">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;" class="table-row"></tr>
      </table>
      <mat-paginator
        [length]="data.totalCount"
        [pageSize]="filter.pageSize"
        [pageIndex]="filter.page! - 1"
        [pageSizeOptions]="[25, 50, 100]"
        (page)="onPage($event)"
        showFirstLastButtons>
      </mat-paginator>
    </div>

    <div class="empty-state" *ngIf="!loading && !data.items.length">
      <mat-icon>receipt_long</mat-icon>
      <p>Nema pronađenih dugovanja</p>
      <button mat-stroked-button color="primary" (click)="openCreate()">Dodaj novo dugovanje</button>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;
    }

    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }

    .page-subtitle {
      margin: 4px 0 0 0;
      color: #6B7280;
      font-size: 14px;
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }

    .create-button, .view-payments-button {
      height: 42px;
      border-radius: 4px;
      font-weight: 600;
    }

    .create-button {
      background-color: #3B82F6 !important;
      color: #FFFFFF !important;
    }

    .view-payments-button {
      border-color: #6B7280 !important;
      color: #374151 !important;
    }

    .create-button mat-icon, .view-payments-button mat-icon {
      margin-right: 8px;
    }

    .filters-container {
      background: white;
      border-radius: 8px;
      padding: 16px 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .filters-row {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .filter-field {
      width: 200px;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      padding: 60px;
    }

    .table-container {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border: 1px solid #E5E7EB;
    }

    .payables-table {
      width: 100%;
      background: white;
    }

    .payables-table th {
      background: #FFFFFF;
      color: #4B5563;
      font-weight: 600;
      padding: 12px 12px !important;
      border-bottom: 1px solid #D1D5DB;
      font-size: 13px;
    }

    .payables-table td {
      padding: 10px 12px !important;
      color: #1F2937;
      font-size: 13px;
      border-bottom: 1px solid #E5E7EB;
    }

    .text-right {
      text-align: right !important;
    }

    .text-center {
      text-align: center !important;
    }

    .amount-cell {
      font-family: 'Roboto Mono', monospace;
      font-weight: 500;
    }

    .amount-cell.paid {
      color: #10B981;
    }

    .amount-cell.remaining {
      color: #F59E0B;
      font-weight: 600;
    }

    .overdue-date {
      color: #DC2626;
      font-weight: 500;
    }

    .overdue-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      color: #DC2626;
      vertical-align: middle;
      margin-left: 4px;
    }

    .status-chip {
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .status-chip.unpaid {
      background: #FEE2E2;
      color: #DC2626;
    }

    .status-chip.partial {
      background: #FEF3C7;
      color: #D97706;
    }

    .status-chip.paid {
      background: #D1FAE5;
      color: #059669;
    }

    .table-actions {
      text-align: right !important;
      width: 130px;
    }

    .action-button {
      transition: all 0.2s ease;
    }

    .action-button:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }

    .action-button.delete-button:hover {
      color: #DC2626;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      background: white;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .empty-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      color: #D1D5DB;
      margin-bottom: 16px;
    }

    .empty-state p {
      color: #6B7280;
      font-size: 16px;
      margin: 0 0 16px 0;
    }

    .date-filters {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #E5E7EB;
    }

    .filter-field-date {
      width: 150px;
    }

    .clear-btn {
      height: 40px;
    }

    .clear-btn mat-icon {
      font-size: 18px;
      margin-right: 4px;
    }
  `]
})
export class PayablesComponent implements OnInit {
  private service = inject(PayablesService);
  private suppliersService = inject(SuppliersService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  cols = ['supplierName', 'documentType', 'invoiceNumber', 'deliveryNoteNumber', 'invoiceDate', 'dueDate', 'amount', 'paidAmount', 'remainingAmount', 'status', 'actions'];
  data: PagedResult<PayableDocument> = { items: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
  suppliers: Supplier[] = [];
  selectedSupplier: Supplier | null = null;
  loading = false;

  filter: PayableFilter = {
    page: 1,
    pageSize: 50
  };

  // Date filter bindings
  invoiceDateFrom: Date | null = null;
  invoiceDateTo: Date | null = null;
  dueDateFrom: Date | null = null;
  dueDateTo: Date | null = null;

  ngOnInit(): void {
    // Load suppliers for dropdown
    this.suppliersService.getAll().subscribe({
      next: (list) => {
        this.suppliers = list;
        // Check for supplierId in query params
        this.route.queryParams.subscribe(params => {
          if (params['supplierId']) {
            this.filter.supplierId = params['supplierId'];
            this.selectedSupplier = this.suppliers.find(s => s.id === params['supplierId']) || null;
          }
          this.load();
        });
      },
      error: () => this.load()
    });
  }

  load() {
    this.loading = true;
    this.service.getFiltered(this.filter).subscribe({
      next: (result) => {
        this.data = result;
        this.loading = false;
      },
      error: () => {
        this.snack.open('Greška pri učitavanju podataka', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  onPage(event: PageEvent) {
    this.filter.page = event.pageIndex + 1;
    this.filter.pageSize = event.pageSize;
    this.load();
  }

  onDateChange() {
    this.filter.invoiceDateFrom = this.invoiceDateFrom?.toISOString().split('T')[0];
    this.filter.invoiceDateTo = this.invoiceDateTo?.toISOString().split('T')[0];
    this.filter.dueDateFrom = this.dueDateFrom?.toISOString().split('T')[0];
    this.filter.dueDateTo = this.dueDateTo?.toISOString().split('T')[0];
    this.filter.page = 1;
    this.load();
  }

  hasDateFilters(): boolean {
    return !!(this.invoiceDateFrom || this.invoiceDateTo || this.dueDateFrom || this.dueDateTo);
  }

  clearDateFilters() {
    this.invoiceDateFrom = null;
    this.invoiceDateTo = null;
    this.dueDateFrom = null;
    this.dueDateTo = null;
    this.filter.invoiceDateFrom = undefined;
    this.filter.invoiceDateTo = undefined;
    this.filter.dueDateFrom = undefined;
    this.filter.dueDateTo = undefined;
    this.filter.page = 1;
    this.load();
  }

  getStatusClass(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Unpaid: return 'unpaid';
      case PaymentStatus.PartiallyPaid: return 'partial';
      case PaymentStatus.Paid: return 'paid';
      default: return '';
    }
  }

  getStatusLabel(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Unpaid: return 'Neplaćeno';
      case PaymentStatus.PartiallyPaid: return 'Delimično';
      case PaymentStatus.Paid: return 'Plaćeno';
      default: return '';
    }
  }

  getDocumentTypeLabel(type: DocumentType | number): string {
    switch (type) {
      case DocumentType.Invoice:
      case 0: return 'Faktura';
      case DocumentType.CashReceipt:
      case 1: return 'Gotovinski';
      case DocumentType.AdvanceInvoice:
      case 2: return 'Avansni';
      case DocumentType.Proforma:
      case 3: return 'Profaktura';
      case DocumentType.CreditNote:
      case 4: return 'Kn. odobrenje';
      case DocumentType.DebitNote:
      case 5: return 'Kn. zaduženje';
      default: return '';
    }
  }

  openCreate() {
    const ref = this.dialog.open(PayableDialogComponent, {
      data: { payable: null, suppliers: this.suppliers, defaultSupplierId: this.filter.supplierId }
    });
    ref.afterClosed().subscribe((dto?: CreatePayableDto) => {
      if (!dto) return;
      this.service.create(dto).subscribe({
        next: () => {
          this.snack.open('Dugovanje je kreirano', 'Zatvori', { duration: 2000 });
          this.load();
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška pri čuvanju', 'Zatvori', { duration: 3000 })
      });
    });
  }

  openEdit(payable: PayableDocument) {
    const ref = this.dialog.open(PayableDialogComponent, {
      data: { payable, suppliers: this.suppliers }
    });
    ref.afterClosed().subscribe((dto?: UpdatePayableDto) => {
      if (!dto) return;
      this.service.update(payable.id, dto).subscribe({
        next: () => {
          this.snack.open('Dugovanje je ažurirano', 'Zatvori', { duration: 2000 });
          this.load();
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška pri čuvanju', 'Zatvori', { duration: 3000 })
      });
    });
  }

  confirmDelete(payable: PayableDocument) {
    const yes = confirm(`Da li ste sigurni da želite da obrišete ovo dugovanje?`);
    if (!yes) return;
    this.service.delete(payable.id).subscribe({
      next: () => {
        this.snack.open('Dugovanje je obrisano', 'Zatvori', { duration: 2000 });
        this.load();
      },
      error: (e) => this.snack.open(e?.error?.message || 'Greška pri brisanju', 'Zatvori', { duration: 3000 })
    });
  }

  viewPayments(payable: PayableDocument) {
    this.dialog.open(PaymentDetailsDialogComponent, {
      data: { payable },
      width: '600px'
    });
  }

  goToPayments() {
    this.router.navigate(['/payments']);
  }
}

@Component({
  selector: 'app-payable-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.payable ? 'Izmeni dugovanje' : 'Novo dugovanje' }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()" class="dialog-form">
      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Dobavljač</mat-label>
          <mat-select formControlName="supplierId" required>
            <mat-option *ngFor="let s of data.suppliers" [value]="s.id">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Tip dokumenta</mat-label>
          <mat-select formControlName="documentType" required>
            <mat-option [value]="0">Faktura</mat-option>
            <mat-option [value]="1">Gotovinski račun</mat-option>
            <mat-option [value]="2">Avansni račun</mat-option>
            <mat-option [value]="3">Profaktura/Predračun</mat-option>
            <mat-option [value]="4">Knjižno odobrenje</mat-option>
            <mat-option [value]="5">Knjižno zaduženje</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Račun broj</mat-label>
          <input matInput formControlName="invoiceNumber">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Otpremnica broj</mat-label>
          <input matInput formControlName="deliveryNoteNumber">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Datum</mat-label>
          <input matInput [matDatepicker]="picker1" formControlName="invoiceDate">
          <mat-datepicker-toggle matSuffix [for]="picker1"></mat-datepicker-toggle>
          <mat-datepicker #picker1></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Datum valute</mat-label>
          <input matInput [matDatepicker]="picker2" formControlName="dueDate">
          <mat-datepicker-toggle matSuffix [for]="picker2"></mat-datepicker-toggle>
          <mat-datepicker #picker2></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Iznos</mat-label>
          <input matInput type="number" step="0.01" formControlName="amount" required>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Plaćeno</mat-label>
          <input matInput type="number" step="0.01" formControlName="paidAmount">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Preostalo (izračunato)</mat-label>
          <input matInput [value]="remainingAmount | number:'1.2-2'" disabled>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Poziv na broj</mat-label>
          <input matInput formControlName="referenceNumber">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Valuta plaćanja</mat-label>
          <mat-select formControlName="paymentCurrency">
            <mat-option value="RSD">RSD</mat-option>
            <mat-option value="EUR">EUR</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Datum izvršenog plaćanja</mat-label>
          <input matInput [matDatepicker]="picker3" formControlName="paymentExecutionDate">
          <mat-datepicker-toggle matSuffix [for]="picker3"></mat-datepicker-toggle>
          <mat-datepicker #picker3></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>ID naloga plaćanja</mat-label>
          <input matInput formControlName="paymentOrderId">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Račun (opciono)</mat-label>
          <input matInput formControlName="extraAccountOrDocRef">
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Napomena</mat-label>
          <textarea matInput rows="2" formControlName="note"></textarea>
        </mat-form-field>
      </div>

      <div class="dialog-actions">
        <button mat-stroked-button mat-dialog-close type="button">Odustani</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">Sačuvaj</button>
      </div>
    </form>
  `,
  styles: [`
    .dialog-form {
      padding: 0 24px 16px 24px;
      display: block;
      width: 700px;
      max-width: 100%;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    .full-width {
      grid-column: 1 / -1;
    }

    .dialog-actions {
      margin-top: 16px;
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }
  `]
})
export class PayableDialogComponent {
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PayableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { payable: PayableDocument | null, suppliers: Supplier[], defaultSupplierId?: string },
  ) {
    if (data.payable) {
      this.form.patchValue({
        supplierId: data.payable.supplierId,
        documentType: data.payable.documentType ?? 0,
        invoiceNumber: data.payable.invoiceNumber,
        deliveryNoteNumber: data.payable.deliveryNoteNumber,
        invoiceDate: data.payable.invoiceDate ? new Date(data.payable.invoiceDate) : null,
        dueDate: data.payable.dueDate ? new Date(data.payable.dueDate) : null,
        amount: data.payable.amount,
        paidAmount: data.payable.paidAmount,
        referenceNumber: data.payable.referenceNumber,
        note: data.payable.note,
        extraAccountOrDocRef: data.payable.extraAccountOrDocRef,
        paymentCurrency: data.payable.paymentCurrency,
        paymentExecutionDate: data.payable.paymentExecutionDate ? new Date(data.payable.paymentExecutionDate) : null,
        paymentOrderId: data.payable.paymentOrderId
      });
    } else if (data.defaultSupplierId) {
      this.form.patchValue({ supplierId: data.defaultSupplierId });
    }
  }

  form = this.fb.group({
    supplierId: ['', [Validators.required]],
    documentType: [0, [Validators.required]],
    invoiceNumber: [''],
    deliveryNoteNumber: [''],
    invoiceDate: [null as Date | null],
    dueDate: [null as Date | null],
    amount: [0, [Validators.required, Validators.min(0.01)]],
    paidAmount: [0, [Validators.min(0)]],
    referenceNumber: [''],
    note: [''],
    extraAccountOrDocRef: [''],
    paymentCurrency: ['RSD'],
    paymentExecutionDate: [null as Date | null],
    paymentOrderId: ['']
  });

  get remainingAmount(): number {
    const amount = this.form.get('amount')?.value || 0;
    const paid = this.form.get('paidAmount')?.value || 0;
    return amount - paid;
  }

  save() {
    if (this.form.invalid) return;
    const v = this.form.value;
    const dto: CreatePayableDto = {
      supplierId: v.supplierId!,
      documentType: v.documentType ?? 0,
      invoiceNumber: v.invoiceNumber || null,
      deliveryNoteNumber: v.deliveryNoteNumber || null,
      invoiceDate: v.invoiceDate ? (v.invoiceDate as Date).toISOString() : null,
      dueDate: v.dueDate ? (v.dueDate as Date).toISOString() : null,
      amount: v.amount!,
      paidAmount: v.paidAmount || 0,
      referenceNumber: v.referenceNumber || null,
      note: v.note || null,
      extraAccountOrDocRef: v.extraAccountOrDocRef || null,
      paymentCurrency: v.paymentCurrency || 'RSD',
      paymentExecutionDate: v.paymentExecutionDate ? (v.paymentExecutionDate as Date).toISOString() : null,
      paymentOrderId: v.paymentOrderId || null
    };
    this.dialogRef.close(dto);
  }
}

@Component({
  selector: 'app-payment-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    DecimalPipe,
    DatePipe
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon class="title-icon">receipt_long</mat-icon>
      Pregled uplata
    </h2>
    <div class="dialog-content">
      <div class="document-info">
        <div class="info-row">
          <span class="label">Račun:</span>
          <span class="value">{{ data.payable.invoiceNumber || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Iznos:</span>
          <span class="value amount">{{ data.payable.amount | number:'1.2-2' }} {{ data.payable.paymentCurrency }}</span>
        </div>
        <div class="info-row">
          <span class="label">Plaćeno:</span>
          <span class="value paid">{{ data.payable.paidAmount | number:'1.2-2' }} {{ data.payable.paymentCurrency }}</span>
        </div>
        <div class="info-row" *ngIf="data.payable.remainingAmount > 0">
          <span class="label">Preostalo:</span>
          <span class="value remaining">{{ data.payable.remainingAmount | number:'1.2-2' }} {{ data.payable.paymentCurrency }}</span>
        </div>
      </div>

      <div class="payments-section">
        <h3>Detalji uplata</h3>

        <!-- Immediate payment -->
        <div class="payment-item immediate" *ngIf="data.payable.paidImmediately">
          <div class="payment-header">
            <mat-icon class="payment-icon immediate-icon">check_circle</mat-icon>
            <div class="payment-info">
              <span class="payment-label">Plaćeno prilikom unosa računa</span>
              <span class="payment-date">{{ data.payable.createdAtUtc | date:'dd.MM.yyyy' }}</span>
            </div>
            <span class="payment-amount immediate-amount">{{ getImmediateAmount() | number:'1.2-2' }}</span>
          </div>
        </div>

        <!-- Payment allocations from payment system -->
        <div class="payment-item" *ngFor="let alloc of data.payable.paymentAllocations">
          <div class="payment-header">
            <mat-icon class="payment-icon">payments</mat-icon>
            <div class="payment-info">
              <span class="payment-label">Uplata {{ alloc.paymentReferenceNumber || '' }}</span>
              <span class="payment-date">{{ alloc.paymentDate | date:'dd.MM.yyyy' }}</span>
            </div>
            <span class="payment-amount">{{ alloc.allocatedAmount | number:'1.2-2' }}</span>
          </div>
          <div class="payment-note" *ngIf="alloc.paymentNote">
            <mat-icon>notes</mat-icon>
            {{ alloc.paymentNote }}
          </div>
        </div>

        <!-- No payments message -->
        <div class="no-payments" *ngIf="!data.payable.paidImmediately && (!data.payable.paymentAllocations || data.payable.paymentAllocations.length === 0)">
          <mat-icon>info</mat-icon>
          <span>Nema evidentiranih uplata za ovaj račun.</span>
        </div>
      </div>
    </div>
    <div class="dialog-actions">
      <button mat-raised-button mat-dialog-close color="primary">Zatvori</button>
    </div>
  `,
  styles: [`
    .title-icon {
      vertical-align: middle;
      margin-right: 8px;
      color: #3B82F6;
    }

    .dialog-content {
      padding: 0 24px;
      min-width: 400px;
    }

    .document-info {
      background: #F9FAFB;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
    }

    .info-row .label {
      color: #6B7280;
      font-size: 13px;
    }

    .info-row .value {
      font-weight: 500;
      color: #1F2937;
    }

    .info-row .value.amount {
      font-family: 'Roboto Mono', monospace;
    }

    .info-row .value.paid {
      color: #10B981;
      font-family: 'Roboto Mono', monospace;
    }

    .info-row .value.remaining {
      color: #F59E0B;
      font-family: 'Roboto Mono', monospace;
    }

    .payments-section h3 {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin: 0 0 12px 0;
    }

    .payment-item {
      background: white;
      border: 1px solid #E5E7EB;
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 8px;
    }

    .payment-item.immediate {
      background: #EFF6FF;
      border-color: #BFDBFE;
    }

    .payment-header {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .payment-icon {
      color: #3B82F6;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .payment-icon.immediate-icon {
      color: #10B981;
    }

    .payment-info {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .payment-label {
      font-weight: 500;
      color: #1F2937;
      font-size: 13px;
    }

    .payment-date {
      font-size: 12px;
      color: #6B7280;
    }

    .payment-amount {
      font-family: 'Roboto Mono', monospace;
      font-weight: 600;
      font-size: 14px;
      color: #10B981;
    }

    .payment-amount.immediate-amount {
      color: #059669;
    }

    .payment-note {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px dashed #E5E7EB;
      font-size: 12px;
      color: #6B7280;
    }

    .payment-note mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .no-payments {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px;
      background: #F9FAFB;
      border-radius: 8px;
      color: #6B7280;
      font-size: 13px;
    }

    .no-payments mat-icon {
      color: #9CA3AF;
    }

    .dialog-actions {
      padding: 16px 24px;
      display: flex;
      justify-content: flex-end;
    }
  `]
})
export class PaymentDetailsDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { payable: PayableDocument }
  ) {}

  getImmediateAmount(): number {
    const totalAllocated = this.data.payable.paymentAllocations?.reduce((sum, a) => sum + a.allocatedAmount, 0) || 0;
    return this.data.payable.paidAmount - totalAllocated;
  }
}
